'use client';

import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { type ComponentPropsWithRef, type ReactNode } from 'react';

import s from './Chip.module.css';

export type ChipBaseProps = {
  ariaDisabled?: boolean;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  isSelected?: boolean;
};

export type ChipProps = ChipBaseProps &
  ComponentPropsWithRef<'button'> & {
    asChild?: boolean;
    isReadOnly?: boolean;
  };

export const chipVariants = cva(s.chip, {
  variants: {
    isReadOnly: {
      true: s.isReadOnly,
    },
    isSelected: {
      true: s.isSelected,
    },
  },
});

export const Chip = (props: ChipProps) => {
  const {
    ariaDisabled,
    asChild = false,
    children,
    className,
    disabled = false,
    isReadOnly = false,
    isSelected,
    onClick,
    ...attrs
  } = props;

  if (!children) {
    return null;
  }

  const handleClick: ComponentPropsWithRef<'button'>['onClick'] = (e) => {
    if (disabled || isReadOnly) {
      e.preventDefault();
      return;
    }

    if (onClick) {
      onClick(e);
    }
  };

  const Component = asChild ? Slot : isReadOnly ? 'span' : 'button';
  const appearsDisabled = ariaDisabled || disabled ? true : undefined;

  return (
    <Component
      aria-disabled={appearsDisabled}
      className={clsx(chipVariants({ isReadOnly, isSelected }), className)}
      data-selected={isSelected}
      disabled={disabled}
      onClick={handleClick}
      {...attrs}
    >
      {children}
    </Component>
  );
};
